import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { site } from "@/data/site";

function Chapter({ chapter, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const flip = index % 2 === 1;

  return (
    <div
      ref={ref}
      className={`grid items-center gap-10 lg:grid-cols-12 lg:gap-0 ${
        flip ? "" : ""
      }`}
      data-testid={`manifesto-chapter-${chapter.num}`}
    >
      <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
        <Reveal>
          <span className="font-display text-7xl font-black text-stroke-gold lg:text-9xl">
            {chapter.num}
          </span>
        </Reveal>
        <Reveal delay={0.1}>
          <h3 className="mt-4 font-display text-2xl font-black uppercase leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">
            {chapter.title}
          </h3>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-md text-base font-medium leading-relaxed text-white/65">
            {chapter.body}
          </p>
        </Reveal>
      </div>

      <div className={`relative lg:col-span-7 ${flip ? "lg:order-1 lg:pr-16" : "lg:pl-16"}`}>
        <Reveal delay={0.15}>
          <div className={`relative overflow-hidden border-2 border-white/10 ${flip ? "clip-diagonal-r" : "clip-diagonal"}`}>
            <motion.img
              style={{ y: imgY }}
              src={site.images[chapter.image]}
              alt={chapter.title}
              className="h-72 w-full scale-110 object-cover grayscale-[45%] sm:h-96 lg:h-[28rem]"
            />
            <div className="absolute inset-0 bg-purple/15 mix-blend-overlay" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#08080A]/70 to-transparent" />
            <div className="halftone-purple absolute inset-0 opacity-50" />
          </div>
          <div
            className={`absolute -z-10 h-full w-full border-2 border-purple/40 ${
              flip ? "-left-3 -top-3" : "-right-3 -top-3"
            }`}
          />
        </Reveal>
      </div>
    </div>
  );
}

export default function Manifesto() {
  return (
    <section id="manifesto" className="relative py-28 lg:py-40" data-testid="manifesto-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <Reveal>
          <p className="mb-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-gold">
            <span className="h-px w-10 bg-gold" />
            The Manifesto
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="max-w-3xl font-display text-4xl font-black uppercase leading-none tracking-tight text-white sm:text-5xl lg:text-6xl">
            Drivers are athletes. <span className="text-purple">Train like one.</span>
          </h2>
        </Reveal>

        <div className="mt-24 flex flex-col gap-28 lg:mt-32 lg:gap-40">
          {site.chapters.map((chapter, i) => (
            <Chapter key={chapter.num} chapter={chapter} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
